'use client';

import { useFormTemplateKey } from '@commenda/api/form-builder/form-template-keys';
import { useFormsTemplate } from '@commenda/api/form-builder/forms-templates';
import {
  Badge,
  Icons,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@commenda/kakashi';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { SubFormFieldBinding } from '@commenda/types';
import { getFieldsFromTemplateDSL } from '../../utils/dsl-traversal';

// Select can't have an empty string as a value, so represent "no binding" sentinel.
const NO_BINDING_VALUE = '__none__';

export function SubFormBindingsEditor({
  keyId,
  fieldBindings,
  availableParentFields,
  onChange,
  preventSameValueForAllBindings,
}: {
  keyId: string;
  fieldBindings: SubFormFieldBinding[];
  availableParentFields: { fieldId: string; label: string }[];
  onChange: (bindings: SubFormFieldBinding[]) => void;
  preventSameValueForAllBindings?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(fieldBindings.length > 0);

  const { key, isLoading: isLoadingKey } = useFormTemplateKey(keyId);
  const { template, isLoading: isLoadingTemplate } = useFormsTemplate(
    key?.liveFormId || null
  );

  const isLoading = isLoadingKey || isLoadingTemplate;

  const subformFields = useMemo(() => {
    if (!template?.dsl) return [];
    return getFieldsFromTemplateDSL(template.dsl);
  }, [template?.dsl]);

  const handleBindingChange = (
    subformFieldName: string,
    parentFieldName: string
  ) => {
    if (parentFieldName === NO_BINDING_VALUE) {
      onChange(
        fieldBindings.filter((b) => b.subformFieldName !== subformFieldName)
      );
      return;
    }
    const existingBinding = fieldBindings.find(
      (b) => b.subformFieldName === subformFieldName
    );
    const existingIndex = fieldBindings.findIndex(
      (b) => b.subformFieldName === subformFieldName
    );
    if (existingIndex >= 0) {
      const newBindings = [...fieldBindings];
      newBindings[existingIndex] = {
        ...existingBinding,
        subformFieldName,
        parentFieldName,
      };
      onChange(newBindings);
    } else {
      onChange([...fieldBindings, { subformFieldName, parentFieldName }]);
    }
  };

  const handlePreventSameValueChange = (
    subformFieldName: string,
    preventSameValue: boolean
  ) => {
    const existingIndex = fieldBindings.findIndex(
      (b) => b.subformFieldName === subformFieldName
    );
    if (existingIndex >= 0) {
      const newBindings = [...fieldBindings];
      newBindings[existingIndex] = {
        ...newBindings[existingIndex],
        preventSameValue,
      };
      onChange(newBindings);
    }
  };

  const handleSameValueErrorMessageChange = (
    subformFieldName: string,
    sameValueErrorMessage: string
  ) => {
    const existingIndex = fieldBindings.findIndex(
      (b) => b.subformFieldName === subformFieldName
    );
    if (existingIndex >= 0) {
      const newBindings = [...fieldBindings];
      newBindings[existingIndex] = {
        ...newBindings[existingIndex],
        sameValueErrorMessage: sameValueErrorMessage || undefined,
      };
      onChange(newBindings);
    }
  };

  const getParentFieldForSubformField = (subformFieldName: string): string => {
    const binding = fieldBindings.find(
      (b) => b.subformFieldName === subformFieldName
    );
    return binding?.parentFieldName || NO_BINDING_VALUE;
  };

  const getBindingForSubformField = (
    subformFieldName: string
  ): SubFormFieldBinding | undefined =>
    fieldBindings.find((b) => b.subformFieldName === subformFieldName);

  if (!keyId) {
    return null;
  }

  return (
    <div className="space-y-4 pt-4 border-t border-border-secondary">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2"
      >
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-text-tertiary" />
        ) : (
          <ChevronRight className="w-4 h-4 text-text-tertiary" />
        )}
        <h4 className="txt-small-medium text-text-secondary">
          Pass Values to Subform
        </h4>
        {fieldBindings.length > 0 && (
          <Badge size="sm" variant="blue">
            {fieldBindings.length}
          </Badge>
        )}
      </button>

      {isExpanded && (
        <div className="space-y-3 pl-6 border-l-2 border-border-secondary">
          <p className="txt-xs-regular text-text-tertiary">
            Pre-fill subform fields with values from parent form fields
          </p>

          {isLoading ? (
            <div className="py-4 text-center">
              <p className="txt-small-regular text-text-tertiary">
                Loading subform fields...
              </p>
            </div>
          ) : subformFields.length === 0 ? (
            <div className="p-3 bg-bg-warning-subtle rounded-lg">
              <p className="txt-xs-regular text-text-warning">
                <Icons.AlertTriangle className="w-3 h-3 inline mr-1" />
                No fields found in the linked form. Make sure the template has
                input fields defined.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {subformFields.map((subformField) => {
                const binding = getBindingForSubformField(subformField.fieldId);
                const hasBoundParent =
                  getParentFieldForSubformField(subformField.fieldId) !==
                  NO_BINDING_VALUE;
                const showPerBindingValidation =
                  hasBoundParent && !preventSameValueForAllBindings;

                return (
                  <div
                    key={subformField.fieldId}
                    className="p-3 rounded-lg border border-border-secondary bg-bg-secondary space-y-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="txt-small-medium text-text-primary">
                        {subformField.label}
                      </span>
                      <span className="txt-xs-regular text-text-tertiary">
                        {subformField.fieldId}
                      </span>
                    </div>
                    <Select
                      value={getParentFieldForSubformField(
                        subformField.fieldId
                      )}
                      onValueChange={(value) =>
                        handleBindingChange(subformField.fieldId, value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select parent field to pass value from" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_BINDING_VALUE}>
                          <span className="text-text-tertiary">No binding</span>
                        </SelectItem>
                        {availableParentFields.map((parentField) => (
                          <SelectItem
                            key={parentField.fieldId}
                            value={parentField.fieldId}
                          >
                            {parentField.label} ({parentField.fieldId})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {showPerBindingValidation && (
                      <div className="pt-2 mt-2 border-t border-border-secondary space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="txt-xs-medium">
                            Prevent same value
                          </Label>
                          <Switch
                            checked={binding?.preventSameValue || false}
                            onCheckedChange={(checked) =>
                              handlePreventSameValueChange(
                                subformField.fieldId,
                                checked
                              )
                            }
                          />
                        </div>
                        {binding?.preventSameValue && (
                          <Input
                            value={binding?.sameValueErrorMessage || ''}
                            onChange={(e) =>
                              handleSameValueErrorMessageChange(
                                subformField.fieldId,
                                e.target.value
                              )
                            }
                            placeholder="Custom error message (optional)"
                            className="txt-xs-regular"
                          />
                        )}
                      </div>
                    )}

                    {hasBoundParent && preventSameValueForAllBindings && (
                      <div className="pt-2 mt-2 border-t border-border-secondary">
                        <p className="txt-xs-regular text-text-tertiary flex items-center gap-1">
                          <Icons.CheckCircle className="w-3 h-3 text-text-success" />
                          Same value validation enabled (global setting)
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}

              {availableParentFields.length === 0 && (
                <div className="p-3 bg-bg-info-subtle rounded-lg">
                  <p className="txt-xs-regular text-text-info">
                    <Icons.InfoCircle className="w-3 h-3 inline mr-1" />
                    Add fields to the parent form to create bindings.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
